/**
 * The public marketing home page — FR-PS-01: the product, the plan comparison
 * and the Create Admin Portal call to action.
 *
 * Rebuilt 26 Aug 2026 from the owner's design handoff
 * (`design_handoff_runnercrew_home`). It is a different surface from the app:
 * flat ground, ZERO radius anywhere, one typeface (Archivo), and a muted teal
 * as a second accent beside the brand yellow. Those tokens live under
 * `ink` / `gold` / `sea` / `ground` / `line` / `copy` in tailwind.config.js —
 * the app's own `night` / `brand` / `flag` scales are untouched, so no console
 * screen moved.
 *
 * Ported from the prototype, not copied: the handoff drives its responsive
 * layout from a JS width listener because its format forbids stylesheets. Here
 * that is ordinary Tailwind breakpoints. Only genuinely scroll-derived state
 * (the shrunk header, the progress bar, the active nav link, the in-view
 * counters) is JavaScript.
 *
 * Breakpoints: the design specifies 1000 / 760 / 520px; this uses Tailwind's
 * `lg` (1024) and `md` (768), which are within a couple of dozen pixels and
 * keep the class strings readable.
 *
 * WHAT IS WIRED: every navigation the routes already support — the anchors,
 * Sign in, Create your portal, and each plan's Choose plan (carrying the plan
 * id into the registration form). "Talk to us" and "Talk to sales" open the
 * enquiry dialog, which files into the Super Admin console. Support is a
 * `mailto:` to SUPPORT_EMAIL now that the club owns runnercrew.in — but it only
 * DELIVERS while the apex carries an MX record forwarding that address to a
 * real mailbox. Resend verification is send-only and creates no inbox.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { platform } from '../lib/api';
import { useDemoAccounts } from '../lib/demo';
import { RunnerCrewMark } from '../components/ui';
import { SalesDialog } from '../components/SalesDialog';
import { SUPPORT_EMAIL } from '../data/constants';

/* ------------------------------------------------------------------ data -- */

const FALLBACK_PLANS = [
  { id: '1', name: 'Free Trial', seats: 30, price: 0, months: 12, isTrial: true, trialDays: 15, trialEvents: 20 },
  { id: '2', name: 'Starter', seats: 100, price: 1500, months: 12 },
  { id: '3', name: 'Growth', seats: 250, price: 2999, months: 12 },
  { id: '4', name: 'Professional', seats: 500, price: 5500, months: 12 },
];

const SECTIONS = [
  ['features', 'Features'],
  ['seats', 'How seats work'],
  ['demo', 'Live demo'],
  ['pricing', 'Pricing'],
];

const BUILT_FOR = [
  'Road crews',
  'Trail collectives',
  'City marathon clubs',
  'Multi-city federations',
  'Corporate run groups',
];

const FEATURES = [
  {
    title: 'Roster & join pipeline',
    body: 'Invitations, join requests and committee activation in one flow. A pending member costs nothing — a seat is charged only when you activate them.',
    tags: ['Bib numbers', 'Tiers & grounds'],
  },
  {
    title: 'Dues & receipts',
    body: 'Club fees tracked as a paid-till date, receipts issued per payment, and a history that survives archives, pauses and expiries.',
    tags: ['Dues reminders', 'Receipt log'],
  },
  {
    title: 'Events & volunteering',
    body: 'Publish events, take RSVPs, staff volunteering departments and count the hours — per club, out of the box.',
    tags: ['RSVP', 'Departments'],
  },
  {
    title: 'Results, PBs & medals',
    body: "Coaches record race results; personal bests and medals land on every runner's card automatically. A coach sees the roster — never the money.",
    tags: ['Coach role', 'PB tracking'],
  },
  {
    title: 'Notices & club chat',
    body: 'Broadcast notices, answer suggestions and run per-club group chat — the committee speaks once, everyone hears it.',
    tags: ['Broadcasts', 'Suggestions'],
  },
  {
    title: 'Archive, restore & trace',
    body: 'Archiving a club is a reversible soft delete: seats release instantly, nothing is destroyed, and the trace log records every change.',
    tags: ['Trace log', 'One-click restore'],
  },
];

const SEAT_POINTS = [
  'Pause a member or archive a club — seats return instantly.',
  'Pending invitations are free. The seat is charged at activation.',
  'The count is live, never stored — so it can never drift.',
];

const POOL = [
  { club: 'Club A — Cubbon Park', seats: 200, pct: 40, tone: 'gold' },
  { club: 'Club B — Kanteerava Track', seats: 100, pct: 20, tone: 'sea' },
  { club: 'Club C — Trail Squad', seats: 150, pct: 30, tone: 'gold' },
];

/**
 * Per-plan selling copy, keyed by plan name. Seats and price always come from
 * the API — only the words live here, so repricing a plan in the Super Admin
 * console changes this page without a deploy.
 *
 * `head` renders as a group header; `strong` items are what the tier ADDS
 * (bold, gold tick), the rest are inherited (regular, teal tick).
 */
const PLAN_COPY = {
  'free trial': {
    tagline: 'The whole platform, on the house',
    forWhom: 'Try it with your real club — nothing to pay, nothing to cancel',
    items: [
      { text: '30 member seats for 15 days', strong: true },
      { text: 'Up to 20 events', strong: true },
      { text: 'Unlimited notices, chat and messages', strong: true },
      { text: 'Every module: dues, results, volunteering' },
      { text: 'Your portal opens instantly — no review' },
      { text: 'Upgrade any time and keep everything' },
    ],
  },
  starter: {
    tagline: 'One club finding its feet',
    forWhom: 'Best for a single committee running one roster',
    items: [
      { text: '100 member seats across unlimited clubs', strong: true },
      { text: 'Full club app: roster, bibs, dues, receipts' },
      { text: 'Events, RSVPs and volunteering hours' },
      { text: 'Race results, personal bests and medals' },
      { text: 'Notices, suggestions and per-club chat' },
      { text: 'Admin, coach and member roles per club' },
      { text: 'Email invitations with secure links' },
      { text: 'Email support' },
    ],
  },
  growth: {
    tagline: 'A club with real momentum',
    forWhom: 'Best for two or three groups across one city',
    head: 'Everything in Starter, plus:',
    items: [
      { text: '250 member seats across unlimited clubs', strong: true },
      { text: 'Multi-club roster and seat dashboard', strong: true },
      { text: 'Automated dues and renewal reminders', strong: true },
      { text: 'Portal trace log and seat reports', strong: true },
      { text: 'Archive and restore clubs, seats released', strong: true },
      { text: 'Priority email support', strong: true },
    ],
  },
  professional: {
    tagline: 'Several clubs, one race calendar',
    forWhom: 'For organisations running a full season across several clubs',
    head: 'Everything in Growth, plus:',
    items: [
      { text: '500 member seats across unlimited clubs', strong: true },
      { text: 'Guided onboarding and roster setup', strong: true },
      { text: 'Coach workflows for results at scale', strong: true },
      { text: 'Participation and dues reporting', strong: true },
      { text: '90% utilisation warnings on the dashboard', strong: true },
      { text: 'Dedicated account manager', strong: true },
      { text: 'Priority support with a named contact', strong: true },
      { text: 'Larger seat blocks on request — talk to sales' },
    ],
  },
};

const TERMS = [
  ['Upgrade mid-term', 'Pay only the difference for the remaining term — your anniversary never moves.'],
  ['Renew any time', 'Renewal extends one year from your anniversary. Reminders at 30, 15, 7 and 1 day.'],
  ['15-day grace', 'After expiry the portal turns read-only, not off. Members still see their own data.'],
  ['90-day retention', 'Everything is kept and fully restorable on renewal. Nothing is deleted quietly.'],
];

const FAQ = [
  ['How does the free trial work?', 'Your portal opens instantly — 15 days, 30 member seats and 20 events, everything else unlimited, no review and no card. Upgrade any time and keep all of it.'],
  ['Does an admin use a seat?', 'Yes — an admin membership is an active membership. Three clubs of your own means three seats.'],
  ['Can one runner join two clubs?', "One account, many clubs — even across organisations. Each club membership takes one seat from the club's own portal."],
  ['How do we pay?', 'By UPI or bank transfer to the account shown on your Subscription tab, then upload the screenshot. The platform owner verifies it and your plan changes that moment; your anniversary never moves.'],
];

/* ----------------------------------------------------------------- brand -- */

function Wordmark({ size = 'md' }) {
  const big = size === 'md';
  return (
    <span className="leading-none">
      <span
        className={`block font-market font-extrabold tracking-[-0.015em] text-white ${
          big ? 'text-[16.5px]' : 'text-[15px]'
        }`}
      >
        RunnerCrew
      </span>
      <span className="mt-[3px] block font-market text-[8.5px] font-bold uppercase tracking-[0.32em] text-gold-500/90">
        Club platform
      </span>
    </span>
  );
}

/* ----------------------------------------------------------------- hooks -- */

/** Scroll-derived chrome: the shrunk bar, the progress ratio, the live section. */
function useScrollChrome() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState('');

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, y / max) : 0);

      // The active link is the LAST section whose top has passed the bar.
      let current = '';
      for (const [id] of SECTIONS) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { scrolled, progress, active };
}

/**
 * True while the element is on screen, false again once it leaves — so the
 * counters and bars inside replay on every scroll past, as the design asks.
 */
function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, inView];
}

const reduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Counts to `to` while `run` is true; snaps back to 0 when it goes false. */
function Count({ to, run, duration = 1200 }) {
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!run) {
      setN(0);
      return;
    }
    if (reduced()) {
      setN(to);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - start) / duration);
      setN(Math.round(to * (1 - (1 - p) ** 3))); // ease-out cubic
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [to, run, duration]);

  return <>{n.toLocaleString('en-IN')}</>;
}

/** A progress track whose fill grows from zero each time it comes into view. */
function Bar({ pct, run, delay = 0, tone = 'gold', height = 'h-2', track = 'bg-line-track' }) {
  const fill =
    tone === 'sea'
      ? 'bg-[linear-gradient(90deg,#0E7C70,#7FD8C8)]'
      : 'bg-[linear-gradient(90deg,#F6C019,#FFD75E)]';
  return (
    <div className={`w-full overflow-hidden ${height} ${track}`}>
      <div
        className={`h-full ${fill} transition-[width] duration-[1200ms] ease-[cubic-bezier(.22,.8,.2,1)] motion-reduce:transition-none`}
        style={{ width: run ? `${pct}%` : '0%', transitionDelay: `${delay}ms` }}
      />
    </div>
  );
}

/* ------------------------------------------------------------- sections -- */

function Header() {
  const { scrolled, progress, active } = useScrollChrome();
  const [menu, setMenu] = useState(false);

  // A panel left open while the layout grows into the desktop nav would hang
  // over the page with no way back to it.
  useEffect(() => {
    const onResize = () => window.innerWidth > 768 && setMenu(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const link =
    'border-b-2 px-[15px] pb-2 pt-[9px] font-market text-[13.5px] font-semibold transition-colors duration-200';

  return (
    <header
      className={`sticky top-0 z-[60] border-b backdrop-blur-[16px] backdrop-saturate-150 transition-[background-color,border-color,box-shadow] duration-[380ms] ease-[cubic-bezier(.22,.7,.2,1)] ${
        scrolled
          ? 'border-white/[.12] bg-ink-900/95 shadow-[0_14px_34px_-22px_rgba(0,0,0,.9)]'
          : 'border-white/[.08] bg-ink-900/90'
      }`}
    >
      <div
        className={`mx-auto flex max-w-[1200px] items-center justify-between px-[18px] transition-[height] duration-[380ms] ease-[cubic-bezier(.22,.7,.2,1)] md:px-[34px] ${
          scrolled ? 'h-[56px] md:h-[58px]' : 'h-[62px] md:h-[72px]'
        }`}
      >
        <Link to="/" className="flex items-center gap-2.5" aria-label="RunnerCrew home">
          <RunnerCrewMark
            className={`transition-transform duration-[380ms] ${scrolled ? 'scale-[.88]' : ''}`}
          />
          <Wordmark />
        </Link>

        {/* desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {SECTIONS.map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              className={`${link} ${
                active === id
                  ? 'border-gold-500 text-white'
                  : 'border-transparent text-white/60 hover:border-gold-500/60 hover:text-white'
              }`}
            >
              {label}
            </a>
          ))}
          <span className="mx-3 h-5 w-px bg-white/[.14]" />
          <Link
            to="/signin"
            className="font-market text-[13.5px] font-bold text-white transition-colors hover:text-gold-500"
          >
            Sign in
          </Link>
          <Link
            to="/register-portal"
            className="ml-3 bg-gold-500 px-5 py-[11px] font-market text-[13px] font-extrabold text-ink-800 transition-all duration-200 hover:-translate-y-px hover:bg-gold-400"
          >
            Create your portal
          </Link>
        </nav>

        {/* hamburger */}
        <button
          type="button"
          className="flex h-[38px] w-11 flex-col items-center justify-center gap-[5px] border border-white/[.18] bg-white/[.06] md:hidden"
          onClick={() => setMenu((m) => !m)}
          aria-expanded={menu}
          aria-label="Menu"
        >
          <span className="h-0.5 w-5 bg-gold-500" />
          <span className="h-0.5 w-5 bg-white/70" />
          <span className="h-0.5 w-5 bg-white/70" />
        </button>
      </div>

      {/* scroll progress */}
      <div
        className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-[linear-gradient(90deg,#F6C019,#7FD8C8)] transition-transform duration-[120ms] ease-linear"
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />

      {menu && (
        <div className="border-t border-white/[.08] bg-ink-400/[.98] px-[18px] pb-[18px] pt-1.5 md:hidden">
          {SECTIONS.map(([id, label]) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={() => setMenu(false)}
              className="block border-b border-white/[.08] py-3.5 font-market text-[15px] font-semibold text-white/80"
            >
              {label}
            </a>
          ))}
          <Link
            to="/signin"
            onClick={() => setMenu(false)}
            className="block border-b border-white/[.08] py-3.5 font-market text-[15px] font-semibold text-white/80"
          >
            Sign in
          </Link>
          <Link
            to="/register-portal"
            onClick={() => setMenu(false)}
            className="mt-4 block bg-gold-500 py-3 text-center font-market text-[14px] font-extrabold text-ink-800"
          >
            Create your portal
          </Link>
        </div>
      )}
    </header>
  );
}

function GlanceCard() {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className="border border-white/[.12] bg-[linear-gradient(160deg,rgba(255,255,255,.07),rgba(255,255,255,.02))] p-6 backdrop-blur-[6px] motion-safe:animate-rise-in [animation-delay:300ms]"
    >
      <div className="flex items-center justify-between">
        <span className="font-market text-[10.5px] font-bold uppercase tracking-[0.26em] text-white/45">
          Portal at a glance
        </span>
        <span className="font-market text-[10.5px] font-bold uppercase tracking-[0.18em] text-sea-300">
          Live
        </span>
      </div>

      <div className="mt-5 flex items-baseline justify-between">
        <span className="font-market text-[13px] text-white/60">Seats used</span>
        <span className="whitespace-nowrap font-market text-[15px] font-extrabold tabular-nums text-white">
          <Count to={450} run={inView} /> / 500
        </span>
      </div>
      <div className="mt-2.5">
        <Bar pct={90} run={inView} track="bg-white/10" />
      </div>
      <p className="mt-2.5 font-market text-[11.5px] text-gold-500/90">
        50 seats remaining — split any way you like
      </p>

      <div className="mt-6 grid grid-cols-2">
        {[
          ['Unlimited', 'Clubs, always free'],
          ['One', 'Login per runner'],
          [<Count key="m" to={24} run={inView} />, 'Modules live'],
          ['Zero', 'Data ever lost'],
        ].map(([value, label], i) => (
          <div
            key={label}
            className={`py-4 ${i % 2 === 0 ? 'pr-4' : 'border-l border-white/[.08] pl-4'} ${
              i < 2 ? 'border-b border-white/[.08]' : ''
            }`}
          >
            <div className="font-market text-[26px] font-extrabold leading-none tabular-nums text-white">
              {value}
            </div>
            <div className="mt-1.5 font-market text-[11px] font-semibold uppercase tracking-[0.16em] text-white/45">
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(168deg,#161C2C_0%,#0D1220_46%,#0A0E19_100%)]">
      {/* drifting glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-56 h-[760px] w-[760px] rounded-full bg-[radial-gradient(circle,rgba(246,192,25,.16),transparent_65%)] motion-safe:animate-drift"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-64 -left-40 h-[640px] w-[640px] rounded-full bg-[radial-gradient(circle,rgba(14,124,112,.28),transparent_65%)] motion-safe:animate-drift-slow"
      />

      <div className="relative mx-auto grid max-w-[1200px] grid-cols-1 items-start gap-[30px] px-[18px] pt-[34px] md:px-[34px] md:pt-14 lg:grid-cols-[1.42fr_1fr] lg:items-end lg:gap-12">
        <div className="pb-10 lg:pb-14">
          <span className="inline-flex items-center gap-2 border border-gold-500/35 bg-gold-500/[.08] px-3.5 py-[7px] motion-safe:animate-fadein">
            <span className="h-1.5 w-1.5 bg-gold-500" />
            <span className="font-market text-[11px] font-bold uppercase tracking-[0.24em] text-gold-500">
              Every club · one platform
            </span>
          </span>

          <h1 className="mt-6 font-market text-[32px] font-extrabold leading-[1.04] tracking-[-0.035em] text-white motion-safe:animate-rise-in sm:text-[40px] lg:text-[52px] xl:text-[64px]">
            The streets are the stadium.
            <br />
            <span className="bg-[linear-gradient(100deg,#FFE383,#F6C019_46%,#7FD8C8)] bg-clip-text text-transparent">
              This is the office.
            </span>
          </h1>

          <p className="mt-6 max-w-[560px] font-market text-[16px] leading-[1.72] text-white/[.66] motion-safe:animate-rise-in md:text-[18.5px] [animation-delay:120ms]">
            Run one club or twenty from a single annual subscription — members,
            dues and receipts, events, race results and volunteering. Every seat
            counted, every rupee on the record.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3.5 motion-safe:animate-rise-in [animation-delay:220ms]">
            <Link
              to="/register-portal"
              className="bg-[linear-gradient(140deg,#FFD75E,#F6C019_60%,#EFB000)] px-[30px] py-[18px] font-market text-[15px] font-extrabold text-ink-800 shadow-[0_14px_34px_-14px_rgba(246,192,25,.95)] transition-transform duration-200 hover:-translate-y-[3px]"
            >
              Create your portal →
            </Link>
            <a
              href="#demo"
              className="border border-white/[.22] px-7 py-[17px] font-market text-[15px] font-bold text-white transition-colors duration-200 hover:border-sea-300/60 hover:bg-sea-300/[.08]"
            >
              Try the live demo
            </a>
          </div>

          <p className="mt-5 font-market text-[12.5px] text-white/40 motion-safe:animate-fadein">
            Free 15-day trial · 30 members · no card required
          </p>
        </div>

        <div className="pb-10 lg:pb-14">
          <GlanceCard />
        </div>
      </div>
    </section>
  );
}

function BuiltFor() {
  return (
    <section className="border-t border-white/[.07] bg-ink-800">
      <div className="mx-auto grid max-w-[1200px] grid-cols-2 px-[18px] md:grid-cols-3 md:px-[34px] lg:grid-cols-[auto_repeat(5,minmax(0,1fr))]">
        <div className="col-span-2 py-3.5 pr-[26px] font-market text-[10.5px] font-bold uppercase tracking-[0.26em] text-white/40 md:col-span-3 lg:col-span-1 lg:whitespace-nowrap">
          Built for
        </div>
        {BUILT_FOR.map((b) => (
          <div
            key={b}
            className="border-l border-white/[.09] px-[18px] py-3.5 font-market text-[13px] font-semibold text-white/[.76]"
          >
            {b}
          </div>
        ))}
      </div>
    </section>
  );
}

function Features() {
  return (
    <section id="features" className="bg-ground">
      <div className="mx-auto max-w-[1200px] px-[18px] pb-[34px] pt-10 md:px-[34px] md:pb-12 md:pt-14">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-[640px]">
            <p className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-sea-600">
              The platform
            </p>
            <h2 className="mt-3 font-market text-[28px] font-extrabold leading-[1.08] tracking-[-0.032em] text-ink-700 md:text-[40px]">
              Everything a committee does, in one place.
            </h2>
          </div>
          <p className="max-w-[330px] font-market text-[15.5px] leading-[1.7] text-copy-muted">
            Six modules ship on every plan. No add-ons, no feature gates — the
            only thing that changes with price is how many runners you seat.
          </p>
        </div>

        {/* the 1px gap over a line-coloured bed IS the rule work */}
        <div className="mt-9 grid grid-cols-1 gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <article
              key={f.title}
              className="bg-white px-6 pb-7 pt-[26px] transition-colors duration-300 hover:bg-ground-warm"
            >
              <span
                className={`grid h-10 w-10 place-items-center font-market text-[13px] font-extrabold ${
                  i % 2 === 0 ? 'bg-gold-50 text-gold-800' : 'bg-sea-50 text-sea-600'
                }`}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="mt-4 font-market text-[19px] font-extrabold tracking-[-0.02em] text-ink-700">
                {f.title}
              </h3>
              <p className="mt-2.5 font-market text-[14.5px] leading-[1.68] text-copy-muted">
                {f.body}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {f.tags.map((t) => (
                  <span
                    key={t}
                    className="bg-line-chip px-[9px] py-[5px] font-market text-[11px] font-bold text-copy"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function SeatPoolCard() {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className="border border-line bg-white shadow-[0_30px_60px_-46px_rgba(16,21,34,.5)]"
    >
      <div className="flex items-center justify-between px-6 pt-6">
        <span className="font-market text-[11px] font-bold uppercase tracking-[0.24em] text-copy-label">
          Bangalore Runners Portal
        </span>
        <span className="whitespace-nowrap font-market text-[12px] font-extrabold text-sea-600">
          Capacity 500
        </span>
      </div>

      <div className="space-y-4 px-6 pt-5">
        {POOL.map((row, i) => (
          <div key={row.club}>
            <div className="flex items-baseline justify-between">
              <span className="font-market text-[14px] font-bold text-ink-700">{row.club}</span>
              <span className="font-market text-[14px] font-extrabold tabular-nums text-copy-muted">
                <Count to={row.seats} run={inView} />
              </span>
            </div>
            <div className="mt-2">
              <Bar
                pct={row.pct}
                run={inView}
                delay={i * 100}
                tone={row.tone}
                height="h-2.5"
                track="bg-line-track"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between px-6 pb-5">
        <span className="font-market text-[13px] font-semibold text-copy-muted">Remaining</span>
        <span className="font-market text-[26px] font-extrabold tabular-nums text-sea-600">
          <Count to={50} run={inView} />
          <span className="ml-1.5 font-market text-[13px] font-semibold">seats</span>
        </span>
      </div>

      <p className="border-t border-gold-100 bg-gold-50 px-6 py-3.5 font-market text-[13px] leading-[1.6] text-gold-900">
        At 90% utilisation the portal dashboard warns you — before Add Member is
        ever blocked.
      </p>
    </div>
  );
}

function Seats() {
  return (
    <section
      id="seats"
      className="border-y border-line bg-[linear-gradient(170deg,#F1F6F4_0%,#FBFAF7_62%)]"
    >
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 items-start gap-10 px-[18px] py-10 md:px-[34px] md:py-[52px] lg:grid-cols-[1fr_1.15fr] lg:gap-12">
        <div>
          <p className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-sea-600">
            How seats work
          </p>
          <h2 className="mt-3 font-market text-[28px] font-extrabold leading-[1.08] tracking-[-0.032em] text-ink-700 md:text-[40px]">
            One seat = one active member in one club.
          </h2>
          <p className="mt-4 font-market text-[16.5px] leading-[1.72] text-copy-muted">
            Your organisation buys a pool of seats once a year. Clubs are free
            and unlimited, and they all draw from that one pool — so a 500-seat
            portal can be 200 + 100 + 150 with 50 spare, rearranged any time.
          </p>
          <ul className="mt-6 space-y-3.5">
            {SEAT_POINTS.map((p) => (
              <li key={p} className="flex gap-3">
                <span className="mt-0.5 grid h-[22px] w-[22px] flex-none place-items-center bg-sea-600 font-market text-[12px] font-bold text-white">
                  ✓
                </span>
                <span className="font-market text-[15px] leading-[1.6] text-copy">{p}</span>
              </li>
            ))}
          </ul>
        </div>

        <SeatPoolCard />
      </div>
    </section>
  );
}

/**
 * See it working — the public demo, on the marketing page.
 *
 * The sign-in screen already offers the two read-only demo logins; a visitor
 * reading this page shouldn't have to find the front door first to discover
 * they exist. It renders at once from constants (useDemoAccounts) and is
 * removed only if the API says the demo was never seeded — waiting for the
 * API meant waiting out a Render cold start, and the section was missing.
 *
 * The buttons don't sign anyone in from here — they land on
 * `/signin?demo=<role>`, which auto-opens the account behind a full-screen
 * hold (the visitor never sees the sign-in form). Signing in involves a
 * splash, a toast and a landing decision, and all of that lives in SignIn's
 * `attempt`; duplicating it on the marketing surface is how the two paths
 * would drift apart.
 */
function Demo() {
  // Drawn at once from constants; the API can only take it away — lib/demo.js.
  const accounts = useDemoAccounts();

  if (accounts.length === 0) return null;

  return (
    <section id="demo" className="border-t border-white/[.07] bg-ink-800">
      {/* `grid-cols-1` is load-bearing: without it the phone column is an
          implicit `auto` track, which grows to the max-content width of the
          nowrap credential line below — 390px on a 375px screen — and the
          whole page scrolled sideways (mobile audit, 6 Sep 2026). */}
      <div className="mx-auto grid grid-cols-1 max-w-[1200px] gap-9 px-[18px] py-12 md:px-[34px] md:py-16 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:gap-14">
        <div className="min-w-0">
          <p className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-gold-500">
            See it working
          </p>
          <h2 className="mt-3 font-market text-[26px] font-extrabold leading-[1.06] tracking-[-0.035em] text-white md:text-[36px]">
            Open the demo club.
          </h2>
          <p className="mt-4 max-w-[460px] font-market text-[14.5px] leading-[1.72] text-white/[.62]">
            A real club with members, events and results — see it as the
            committee and as a runner. Read-only: you can open every screen,
            nothing can be changed.
          </p>
        </div>

        <div className="min-w-0 border border-white/[.14] bg-white/[.05] px-5 md:px-6">
          {accounts.map((a) => (
            <div
              key={a.email}
              className="flex items-center gap-4 border-t border-white/[.12] py-4 first:border-t-0 md:py-5"
            >
              <div className="min-w-0 flex-1">
                <div className="font-market text-[14px] font-extrabold text-white">{a.label}</div>
                {/* wraps rather than truncates: the password is the point */}
                <div className="mt-0.5 font-market text-[11.5px] tabular-nums text-white/40 [overflow-wrap:anywhere]">
                  {a.email} · {a.password}
                </div>
              </div>
              <Link
                to={`/signin?demo=${a.role}`}
                className="flex-none bg-gold-500 px-5 py-2.5 font-market text-[11px] font-extrabold uppercase tracking-[0.14em] text-ink-800 transition-all duration-200 hover:brightness-110"
              >
                Open
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanColumn({ plan, selected, onSelect }) {
  const copy = PLAN_COPY[plan.name.toLowerCase()] ?? {
    tagline: `${plan.seats.toLocaleString('en-IN')} seats`,
    forWhom: 'Unlimited clubs, one annual term.',
    items: [{ text: `${plan.seats.toLocaleString('en-IN')} member seats`, strong: true }],
  };
  const trial = Boolean(plan.isTrial);
  const monthly = Math.round(Number(plan.price) / 12);
  const perSeat = Math.round(Number(plan.price) / plan.seats);
  const scale = plan.seats / 100;
  const flagged = plan.name === 'Professional';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(plan.id)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(plan.id))}
      className={`relative flex cursor-pointer flex-col px-[22px] pb-7 pt-[26px] transition-[background,box-shadow] duration-[350ms] ${
        selected
          ? 'z-[2] bg-[linear-gradient(178deg,#151C2C_0%,#0B0F18_100%)] shadow-[0_0_0_2px_#F6C019,0_30px_56px_-34px_rgba(11,15,24,.8)] motion-safe:animate-pop'
          : 'bg-white'
      }`}
    >
      {flagged && (
        <span
          className={`absolute right-0 top-0 px-2.5 py-[5px] font-market text-[9.5px] font-extrabold uppercase tracking-[0.18em] ${
            selected ? 'bg-ink-700 text-gold-500' : 'bg-gold-500 text-ink-700'
          }`}
        >
          Most chosen
        </span>
      )}

      <h3
        className={`font-market text-[20px] font-extrabold tracking-[-0.02em] [overflow-wrap:anywhere] ${
          selected ? 'text-white' : 'text-ink-700'
        }`}
      >
        {plan.name}
      </h3>
      <p
        className={`mt-1.5 min-h-[36px] font-market text-[12.5px] leading-[1.45] ${
          selected ? 'text-white/60' : 'text-copy-muted'
        }`}
      >
        {copy.tagline}
      </p>

      <div className="mt-3 flex items-baseline gap-1.5">
        <span
          className={`font-market text-[27px] font-extrabold tracking-[-0.03em] tabular-nums ${
            selected ? 'text-gold-500' : 'text-ink-700'
          }`}
        >
          ₹{trial ? '0' : monthly.toLocaleString('en-IN')}
        </span>
        <span
          className={`font-market text-[12.5px] font-semibold ${
            selected ? 'text-white/50' : 'text-copy-muted'
          }`}
        >
          {trial ? `/ ${plan.trialDays ?? 15} days` : '/ month'}
        </span>
      </div>
      <p
        className={`mt-1 min-h-[33px] font-market text-[11.5px] font-semibold ${
          selected ? 'text-white/50' : 'text-copy-muted'
        }`}
      >
        {trial ? (
          <>Nothing billed · no card required</>
        ) : (
          <>
            ₹{Number(plan.price).toLocaleString('en-IN')} billed annually · ₹
            {perSeat.toLocaleString('en-IN')} per seat
          </>
        )}
      </p>

      {/* The label never changes to "Selected" — it is the action, not a state.
          Every plan starts the same way: the portal opens on the free trial
          and the paid plan is picked from inside (owner's direction, 6 Sep
          2026), so there is no plan id to carry across. */}
      <Link
        to="/register-portal"
        onClick={(e) => e.stopPropagation()}
        className={`mt-3 flex h-[50px] items-center justify-center whitespace-nowrap font-market text-[14px] font-extrabold transition-all duration-200 hover:-translate-y-px hover:brightness-105 ${
          selected
            ? 'bg-[linear-gradient(140deg,#FFD75E,#F6C019_58%,#EFB000)] text-ink-800 shadow-[0_12px_26px_-14px_rgba(246,192,25,.9)]'
            : 'border border-line-strong bg-white text-ink-700'
        }`}
      >
        {trial ? 'Start free trial' : 'Start with a free trial'}
      </Link>

      <div className="mt-4 flex h-8 items-center justify-between">
        <span className="flex items-baseline gap-1.5">
          <span
            className={`font-market text-[27px] font-extrabold tabular-nums ${
              selected ? 'text-white' : 'text-ink-700'
            }`}
          >
            {plan.seats.toLocaleString('en-IN')}
          </span>
          <span
            className={`font-market text-[11px] font-bold uppercase tracking-[0.2em] ${
              selected ? 'text-white/50' : 'text-copy-muted'
            }`}
          >
            Seats
          </span>
        </span>
        <span
          className={`whitespace-nowrap px-2 py-1 font-market text-[10px] font-extrabold ${
            selected ? 'bg-sea-300/[.14] text-sea-300' : 'bg-sea-50 text-sea-600'
          }`}
        >
          {trial ? 'FREE' : scale === 1 ? 'BASE' : `${scale % 1 ? scale.toFixed(1) : scale}×`}
        </span>
      </div>

      <p
        className={`mt-2 font-market text-[12px] font-semibold ${
          selected ? 'text-white/50' : 'text-copy-muted'
        }`}
      >
        {trial
          ? `Up to ${plan.trialEvents ?? 20} events · upgrade any time`
          : 'Unlimited clubs · 1 year term'}
      </p>

      <div className={`my-4 h-px ${selected ? 'bg-white/[.16]' : 'bg-line-soft'}`} />

      <ul className="flex flex-col gap-[13px]">
        {copy.head && (
          <li
            className={`font-market text-[11px] font-extrabold uppercase tracking-[0.16em] ${
              selected ? 'text-white/60' : 'text-copy-label'
            }`}
          >
            {copy.head}
          </li>
        )}
        {copy.items.map((it) => (
          <li key={it.text} className="flex gap-2.5">
            <span
              className={`font-market text-[13px] leading-[1.5] ${
                it.strong
                  ? selected
                    ? 'text-gold-500'
                    : 'text-gold-800'
                  : selected
                    ? 'text-sea-300'
                    : 'text-sea-600'
              }`}
            >
              ✓
            </span>
            <span
              className={`font-market text-[13.5px] leading-[1.5] ${it.strong ? 'font-bold' : 'font-normal'} ${
                selected ? 'text-white/[.86]' : 'text-copy'
              }`}
            >
              {it.text}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The design draws three plans; the API currently serves four. Column count
 * follows the data rather than the mock, and the classes are spelled out in
 * full because Tailwind scans source text — a computed `lg:grid-cols-${n}`
 * would never be generated.
 */
const LG_COLS = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
};

function Pricing({ plans }) {
  const professional = plans.find((p) => p.name === 'Professional');
  const [selected, setSelected] = useState(professional?.id ?? plans[0]?.id);

  useEffect(() => {
    if (!plans.some((p) => p.id === selected)) {
      setSelected(plans.find((p) => p.name === 'Professional')?.id ?? plans[0]?.id);
    }
  }, [plans, selected]);

  return (
    <section id="pricing" className="bg-ground">
      <div className="mx-auto max-w-[1200px] px-[18px] pb-[34px] pt-10 md:px-[34px] md:pt-[52px]">
        <p className="font-market text-[11px] font-bold uppercase tracking-[0.28em] text-sea-600">
          Pricing
        </p>
        <h2 className="mt-3 max-w-[640px] font-market text-[28px] font-extrabold leading-[1.08] tracking-[-0.032em] text-ink-700 md:text-[40px]">
          One price a year. Seats, not surprises.
        </h2>
        <p className="mt-4 max-w-[720px] font-market text-[17px] leading-[1.7] text-copy-muted">
          Every portal starts free — 15 days, 30 members, 20 events, no card,
          no review. Then pick the seat count you need from inside your portal
          and pay by UPI or bank transfer: the whole club platform ships on
          every plan. Monthly figures are the annual price divided by twelve;
          billing is once a year.
        </p>

        <div className={`mt-9 grid grid-cols-1 gap-px border border-line bg-line md:grid-cols-2 ${LG_COLS[Math.min(plans.length, 4)]}`}>
          {plans.map((plan) => (
            <PlanColumn
              key={plan.id}
              plan={plan}
              selected={plan.id === selected}
              onSelect={setSelected}
            />
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 border border-line bg-white md:grid-cols-2 lg:grid-cols-4">
          {TERMS.map(([title, body], i) => (
            <div
              key={title}
              className={`px-5 py-[18px] ${i < TERMS.length - 1 ? 'lg:border-r lg:border-line' : ''} ${
                i % 2 === 0 ? 'md:border-r md:border-line' : ''
              }`}
            >
              <h4 className="font-market text-[13.5px] font-extrabold text-ink-700">{title}</h4>
              <p className="mt-1.5 font-market text-[13px] leading-[1.6] text-copy-muted">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Faq() {
  return (
    <section className="bg-ground">
      <div className="mx-auto max-w-[1200px] px-[18px] pb-12 md:px-[34px]">
        {/* 4 items: 2×2 at md, one row at lg — every column filled, so the
            gap-px backing never shows through as a beige block. */}
        <div className="grid grid-cols-1 gap-px border border-line bg-line md:grid-cols-2 lg:grid-cols-4">
          {FAQ.map(([q, a]) => (
            <div key={q} className="bg-ground px-6 py-[22px]">
              <h3 className="font-market text-[15px] font-extrabold text-ink-700">{q}</h3>
              <p className="mt-2 font-market text-[14px] leading-[1.65] text-copy-muted">{a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Poster({ onTalk }) {
  return (
    <section className="relative overflow-hidden bg-[linear-gradient(130deg,#FFD75E_0%,#F6C019_52%,#EFB000_100%)]">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-32 -top-52 h-[620px] w-[620px] rounded-full bg-[radial-gradient(circle,rgba(255,255,255,.42),transparent_65%)]"
      />
      <div className="relative mx-auto max-w-[1200px] px-[18px] py-12 md:px-[34px] md:py-16">
        <h2 className="max-w-[860px] font-market text-[28px] font-extrabold leading-[1.04] tracking-[-0.038em] text-ink-800 md:text-[44px] lg:text-[50px]">
          Every club. One platform. Every rupee on the record.
        </h2>
        <div className="mt-8 flex flex-wrap gap-3.5">
          <Link
            to="/register-portal"
            className="bg-ink-800 px-[30px] py-[18px] font-market text-[15px] font-extrabold text-gold-500 transition-all duration-200 hover:-translate-y-[3px] hover:bg-ink-700"
          >
            Create your portal →
          </Link>
          <button
            type="button"
            onClick={onTalk}
            className="border-[1.5px] border-ink-800/40 px-7 py-[17px] font-market text-[15px] font-bold text-ink-800 transition-colors duration-200 hover:border-ink-800"
          >
            Talk to us
          </button>
        </div>
      </div>
    </section>
  );
}

function Footer({ onTalk }) {
  const col = 'font-market text-[10.5px] font-bold uppercase tracking-[0.24em] text-white/50';
  const item = 'block py-1.5 font-market text-[13.5px] text-white/[.62] transition-colors hover:text-gold-500';
  return (
    <footer className="bg-ink-800">
      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-[18px] py-10 md:grid-cols-2 md:px-[34px] lg:grid-cols-[1.4fr_1fr_1fr_1fr]">
        <div>
          <div className="flex items-center gap-2.5">
            <RunnerCrewMark size={30} />
            <span className="font-market text-[15px] font-extrabold text-white">RunnerCrew</span>
          </div>
          <p className="mt-4 max-w-[300px] font-market text-[13.5px] leading-[1.7] text-white/55">
            The club-management platform for running organisations. Multi-club,
            multi-tenant, seat-based.
          </p>
        </div>

        <div>
          <p className={col}>Platform</p>
          <div className="mt-3">
            <a href="#features" className={item}>Features</a>
            <a href="#seats" className={item}>How seats work</a>
            <a href="#demo" className={item}>Live demo</a>
            <a href="#pricing" className={item}>Pricing</a>
          </div>
        </div>

        <div>
          <p className={col}>For clubs</p>
          <div className="mt-3">
            <Link to="/signin" className={item}>Member sign in</Link>
            <Link to="/signin" className={item}>Admin sign in</Link>
            <Link to="/register-portal" className={item}>Create your portal</Link>
          </div>
        </div>

        <div>
          <p className={col}>Contact</p>
          <div className="mt-3">
            {/* Live as of the runnercrew.in domain. This link only WORKS while
                the apex carries an MX record forwarding support@ to a real
                mailbox — Resend verification covers sending, never receiving.
                `dig +short MX runnercrew.in` must not be empty. */}
            <a href={`mailto:${SUPPORT_EMAIL}`} className={item}>Support</a>
            <button type="button" onClick={onTalk} className={`${item} text-left`}>
              Talk to sales
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ page -- */

export function HomePage() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [salesOpen, setSalesOpen] = useState(false);

  const load = useCallback(() => {
    let alive = true;
    platform
      .plans()
      .then((res) => alive && res.plans?.length && setPlans(res.plans))
      .catch(() => {}); // the fallback list is already on screen
    return () => {
      alive = false;
    };
  }, []);

  useEffect(load, [load]);

  return (
    <div className="min-h-screen bg-ground font-market text-ink-700">
      <Header />
      <main>
        <Hero />
        <BuiltFor />
        <Features />
        <Seats />
        <Demo />
        <Pricing plans={plans} />
        <Faq />
        <Poster onTalk={() => setSalesOpen(true)} />
      </main>
      <Footer onTalk={() => setSalesOpen(true)} />
      {salesOpen && <SalesDialog onClose={() => setSalesOpen(false)} />}
    </div>
  );
}
